/*  src/containers/Login/Login.js  */

import React, { Component } from "react";
import { connect } from "react-redux";
import * as actions from "../../store/actions";
import ReCAPTCHA from "react-google-recaptcha";
import * as extensionsReq from "../../axios/requests/extensionsReq";
import * as loginReq from "../../axios/requests/loginReq";
import Spinner from "../../components/UI/Spinner/Spinner";
import Input from "../../components/UI/Input/Input";
import classes from "./Login.module.css";
import { withTranslation } from "react-i18next";
import i18next from "i18next";
import myLogo from '../../assets/logo/logo.png'

const reCaptchaSitekey = "6LeBK5AcAAAAAGb0sZHvmSBlWRkXzPL4K2TsoY1N";

/* ------------------------------------------------------------------ */
/*  Stateless helpers                                                  */
/* ------------------------------------------------------------------ */

const Agreement = ({ t, terms, privacy, toggle }) => (
    <section className={classes.agreementSection}>
        <h2 className={classes.agreementTitle}>
            {t("Please read Effective app agreement")}
        </h2>
        <p className={classes.agreementDesc}>
            {t(
                "To use the application, you must agree to the terms of use and confirm reading the privacy policy"
            )}
        </p>

        <label className={classes.checkboxRow}>
            <input
                type="checkbox"
                checked={terms}
                onChange={() => toggle("terms")}
            />
            <span>
                {t("I have read and agree to the")}{" "}
                <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.itcweb.us/user-policy/"
                >
                    {t("Terms of use")}
                </a>
            </span>
        </label>

        <label className={classes.checkboxRow}>
            <input
                type="checkbox"
                checked={privacy}
                onChange={() => toggle("privacy-policy")}
            />
            <span>
                {t("I have read and agree to the")}{" "}
                <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.itcweb.us/privacy-policy/"
                >
                    {t("privacy policy")}
                </a>
            </span>
        </label>
    </section>
);

/* ------------------------------------------------------------------ */
/*  Container component (business‑logic unchanged)                     */
/* ------------------------------------------------------------------ */

class Login extends Component {
    state = {
        controls: {
            email: {
                elementType: "input",
                elementConfig: { type: "email", placeholder: this.props.t("Email") },
                value: "",
                validation: { required: true, isEmail: true },
                valid: false,
                touched: false,
            },
            password: {
                elementType: "input",
                elementConfig: { type: "password", placeholder: this.props.t("Password") },
                value: "",
                validation: { required: true, minLength: 8 },
                valid: false,
                touched: false,
            },
        },
        extensions: undefined,
        selectedDevice: undefined,
        token: undefined,
        errorMsg: undefined,
        ReCaptchaValue: "",
        isAgreementTouched: false,
        isPrivacyPolicyAccepted: false,
        allowToLoginApp: false,
        isTermOfUseAccepted: false,
        loginPage: "Login", // [Login, Device, Agreement]
        selectedLangCode: "he",// [he, en]
        errorKey: null,        // i18n key         (string)
        errorParams: undefined // optional params  (object)
    };

    /* -------------------- life‑cycle -------------------- */
    componentDidMount() {
        const savedLang = localStorage.getItem("app-language") ?? i18next.language;
        this.onSelectNewLangHandler(savedLang);
    }

    /* -------------------- helpers ----------------------- */
    checkValidity = (value, rules) => {
        if (!rules) return true;
        let ok = true;
        if (rules.required) ok = value.trim() !== "" && ok;
        if (rules.minLength) ok = value.length >= rules.minLength && ok;
        if (rules.maxLength) ok = value.length <= rules.maxLength && ok;
        if (rules.isEmail) ok = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value) && ok;
        if (rules.isNumeric) ok = /^\d+$/.test(value) && ok;
        return ok;
    };

    inputChangedHandler = (e, name) => {
        const updated = {
            ...this.state.controls,
            [name]: {
                ...this.state.controls[name],
                value: e.target.value,
                valid: this.checkValidity(e.target.value, this.state.controls[name].validation),
                touched: true,
            },
        };
        this.setState({ controls: updated });
    };

    onChangeReCaptcha = (val) => this.setState({ ReCaptchaValue: val });

    onSelectNewLangHandler = (langCode) => {
        // persist the choice
        localStorage.setItem('app-language', langCode);

        // changeLanguage is async – use its callback
        this.props.i18n.changeLanguage(langCode, () => {
            const t = this.props.i18n.t.bind(this.props.i18n);

            // refresh the placeholders inside state
            this.setState(prev => ({
                selectedLangCode: langCode,
                controls: {
                    ...prev.controls,
                    email: {
                        ...prev.controls.email,
                        elementConfig: {
                            ...prev.controls.email.elementConfig,
                            placeholder: t('Email')
                        }
                    },
                    password: {
                        ...prev.controls.password,
                        elementConfig: {
                            ...prev.controls.password.elementConfig,
                            placeholder: t('Password')
                        }
                    }
                }
            }));
        });
    };

    handleCheckBoxChange = (which) => {
        if (which === "terms")
            this.setState((s) => ({ isTermOfUseAccepted: !s.isTermOfUseAccepted }));
        if (which === "privacy-policy")
            this.setState((s) => ({ isPrivacyPolicyAccepted: !s.isPrivacyPolicyAccepted }));
    };

    /* -------------------- submit flow ------------------- */
    loginHandler = async (e) => {
        e.preventDefault();
        const page = this.state.loginPage;
        try {
            if (page === "Agreement") return this.agreementNext(this.state.ReCaptchaValue);
            if (page === "Login") return this.loginStep(this.state.ReCaptchaValue);
            return this.deviceStep();
        } catch (err) {
            const agentName = err.response?.data?.match(/Agent (\w+) online/)?.[1];
            this.setState({
                errorKey: 'Please check if someone already login to the extension',
                errorParams: { agent: agentName }
            });
        }
    };

    agreementNext = async (captcha) => {
        try {
            const { isTermOfUseAccepted, isPrivacyPolicyAccepted } = this.state;
            if (!isTermOfUseAccepted || !isPrivacyPolicyAccepted)
                return this.setState({
                    errorKey: "To enter the system, you must confirm the terms of use and the privacy policy", errorParams: undefined
                });


            await loginReq.agreementAcception(
                this.state.controls.email.value,
                this.state.controls.password.value,
                isTermOfUseAccepted,
                isPrivacyPolicyAccepted
            );
            const user = await loginReq.loginReq(
                this.state.controls.email.value,
                this.state.controls.password.value,
                captcha
            );
            this.setState({ loginPage: "Device" });   // <‑‑ move to next step
            this.fetchDevices(user);
        } catch (e) {
            this.setState({ errorKey: e.message || e })
        }
    };

    loginStep = async (captcha) => {
        try {
            const user = await loginReq.loginReq(
                this.state.controls.email.value,
                this.state.controls.password.value,
                captcha
            );
            const { isPrivacyPolicyAccepted, isTermOfUseAccepted } = user.user || {};
            if (!isPrivacyPolicyAccepted || !isTermOfUseAccepted)
                return this.setState({ isAgreementTouched: true, loginPage: "Agreement" });

            this.setState({ isPrivacyPolicyAccepted, isTermOfUseAccepted });
            this.fetchDevices(user);
        } catch (e) {
            this.setState({ errorKey: e.message || e })
        }
    };

    deviceStep = async () => {
        try {
            const { selectedDevice, extensions, token } = this.state;
            if (!selectedDevice)
                return this.setState({ errorKey: 'Please select extension.', errorParams: undefined });

            if (selectedDevice !== "Empty") {
                const found = extensions.find((e) => e.device === selectedDevice.device);
                if (!found) return this.setState({ errorKey: "device not found.", errorParams: undefined });
                if (found.onlineAgent)
                    return this.setState({ errorKey: `Agent ${found.onlineAgent} is logged in.`, errorParams: undefined });
            }

            await extensionsReq.setOnlineAgent(
                selectedDevice === "Empty" ? "Empty" : selectedDevice.device,
                token
            );

            this.props.onLogin(
                this.state.controls.email.value,
                this.state.controls.password.value,
                true,
                this.state.ReCaptchaValue
            );
        } catch (e) {
            this.setState({ errorKey: e.message || e })
        }
    };

    async fetchDevices(user) {
        const token = user.token;
        const exts = await extensionsReq.getExtensions(token);
        const last = exts.find((e) => e.device === user.user.device) || "Empty";
        this.props.fetchCompanyExtsSuccess(exts);
        this.setState({
            extensions: exts,
            selectedDevice: last,
            token,
            loginPage: "Device",
            allowToLoginApp: true,
            errorMsg: undefined,
            errorKey: undefined
        });
    }

    HandleOnSelectDevice = (e) => {
        const { extensions } = this.state;
        const val = e.target.value;
        const chosen = val === "EMPTY" ? "Empty" : extensions.find((d) => d.device === val);
        this.setState({ selectedDevice: chosen });
    };

    /* -------------------- rendering helpers -------------- */
    buildControls() {
        return Object.entries(this.state.controls).map(([id, cfg]) => (
            <Input
                key={id}
                elementType={cfg.elementType}
                elementConfig={cfg.elementConfig}
                value={cfg.value}
                invalid={!cfg.valid}
                shouldValidate={cfg.validation}
                touched={cfg.touched}
                changed={(e) => this.inputChangedHandler(e, id)}
                className={classes.textInput}
            />
        ));
    }

    buildDeviceSelector(t) {
        const { extensions = [], selectedDevice } = this.state;
        return (
            <select
                className={classes.deviceSelect}
                value={
                    selectedDevice === "Empty"
                        ? "EMPTY"
                        : selectedDevice?.device || "UNSELECTED"
                }
                onChange={this.HandleOnSelectDevice}
            >
                <option disabled value="UNSELECTED">
                    {t("No extension")}
                </option>
                <option value="EMPTY">{t("Without Extension")}</option>
                {extensions.map((ext) => (
                    <option
                        key={ext._id}
                        value={ext.device}
                        disabled={!!ext.onlineAgent}
                    >
                        {ext.onlineAgent
                            ? `(${t("Connected")}) ${ext.device} (${ext.shortNum})`
                            : `${ext.device} (${ext.shortNum})`}
                    </option>
                ))}
            </select>
        );
    }

    /* -------------------- render ------------------------- */
    render() {
        const { t } = this.props;
        const s = this.state;

        const stage =
            this.state.loginPage === "Agreement"
                ? "AGREEMENT"
                : this.state.loginPage === "Login"
                    ? "LOGIN"
                    : "DEVICE";

        return (
            <div className={classes.wrapper} dir={this.state.selectedLangCode === "he" ? "rtl" : "ltr"}>
                <div
                    className={classes.card}
                    dir={s.selectedLangCode === "he" ? "rtl" : "ltr"}
                >

                    <div className={classes.langSelectWrap}>
                        <select
                            value={this.state.selectedLangCode}
                            onChange={(e) => this.onSelectNewLangHandler(e.target.value)}
                            className={classes.langSelect}
                        >
                            <option value="he">{t("he")}</option>
                            <option value="en">{t("en")}</option>
                        </select>
                    </div>
                    {/* -------- header text -------- */}

                    <div className={classes.header}>
                        <h1 className={classes.headingXL}>
                            <img className={classes['company-logo']} src={myLogo} />
                            {/* {t("Welcome Back")} <span role="img" aria-label="wave">👋</span> */}
                        </h1>
                        {stage === "LOGIN" && <p className={classes.subtitle}>{t("Please login to your account")}</p>}
                    </div>


                    {s.errorKey && (
                        <p className={classes.error}>
                            {t(s.errorKey, s.errorParams)}
                        </p>
                    )}

                    {/* -------- form -------- */}
                    <form onSubmit={this.loginHandler} className={classes.form}>
                        {/* ---------- login step ---------- */}
                        {stage === "LOGIN" &&
                            Object.entries(this.state.controls).map(([id, cfg]) => (
                                <div key={id} className={classes.formControl}>
                                    <label>{cfg.elementConfig.placeholder}</label>
                                    <input
                                        type={cfg.elementConfig.type}
                                        value={cfg.value}
                                        onChange={(e) => this.inputChangedHandler(e, id)}
                                    />
                                </div>
                            ))}

                        {stage === "LOGIN" && (
                            <div className={classes.recaptchaWrapper}>
                                <ReCAPTCHA sitekey={reCaptchaSitekey} onChange={this.onChangeReCaptcha} />
                            </div>
                        )}

                        {/* ---------- device step ---------- */}
                        {stage === "DEVICE" && (
                            <div className={classes.formControl}>
                                <label>{t("Extension")}</label>
                                <select
                                    className={classes.deviceSelect}
                                    value={
                                        this.state.selectedDevice === "Empty"
                                            ? "EMPTY"
                                            : this.state.selectedDevice?.device || "UNSELECTED"
                                    }
                                    onChange={this.HandleOnSelectDevice}
                                >
                                    {/* <option disabled value="UNSELECTED">
                                        {t("No extension")}
                                    </option> */}
                                    <option value="EMPTY">{t("Without Extension")}</option>
                                    {this.state.extensions?.map((ext) => (
                                        <option
                                            key={ext._id}
                                            value={ext.device}
                                            disabled={!!ext.onlineAgent}
                                        >
                                            {ext.onlineAgent
                                                ? `(${t("Connected")}) ${ext.device} (${ext.shortNum})`
                                                : `${ext.device} (${ext.shortNum})`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* ---------- agreement step ---------- */}
                        {stage === "AGREEMENT" && (

                            <Agreement
                                t={t}
                                terms={this.state.isTermOfUseAccepted}
                                privacy={this.state.isPrivacyPolicyAccepted}
                                toggle={this.handleCheckBoxChange}
                            />

                        )}

                        {/* submit / spinner */}
                        {this.props.loading ? <Spinner /> : (
                            <button type="submit" className={classes.submitBtn}>
                                {stage === "DEVICE" ? t("Next") : t("Login")}
                            </button>
                        )}
                    </form>

                    {/* -------- bottom line -------- */}
                    {/* <p className={classes.bottomLine}>
                        {t("Don't have an account?")} <a href="#reg">{t("Register")}</a>
                    </p> */}
                </div>
            </div>
        );
    }
}

/* -------------------- redux wiring -------------------- */
const mapState = (state) => ({
    loading: state.login.loading,
    error: state.login.error,
    isBtnLogginTouched: state.login.isBtnLogginTouched,
});
const mapDispatch = (d) => ({
    onLogin: (e, p, t, c) => d(actions.login(e, p, t, c)),
    fetchCompanyExtsSuccess: (exts) => d(actions.fetchCompanyExtsSuccess(exts)),
});

export default withTranslation("common")(connect(mapState, mapDispatch)(Login));
